import React, { useState, useEffect, useMemo } from 'react';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Table from '../../components/common/Table/Table';
import Modal from '../../components/common/Modal/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import { getAllCounsellors, createCounsellor, deleteCounsellor, suspendCounsellor, unsuspendCounsellor } from '../../services/counsellorService';
import useForm from '../../hooks/useForm';
import useAuth from '../../hooks/useAuth';
import { useToast, useConfirm } from '../../context/ToastContext';
import styles from './Mentors.module.css';

const Counsellors = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [counsellors, setCounsellors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCredentials, setShowCredentials] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const toast = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    fetchCounsellors();
  }, []);

  const fetchCounsellors = async () => {
    try {
      const data = await getAllCounsellors();
      setCounsellors(data);
    } catch (error) {
      console.error('Error fetching counsellors:', error);
    } finally {
      setLoading(false);
    }
  };

  const { values, handleChange, handleSubmit, resetForm , isSubmitting} = useForm(
    {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      linkedin_profile: '',
      description: '',
      specialization: '',
      years_of_experience: 0,
      timezone: ''
    },
    async (formValues) => {
      try {
        const result = await createCounsellor(formValues);
        setShowCredentials(result.credentials);
        setShowModal(false);
        resetForm();
        fetchCounsellors();
      } catch (error) {
        toast.error('Error creating counsellor: ' + error.message);
      }
    }
  );

  const handleDelete = async (counsellorId) => {
    const confirmed = await confirm('Are you sure you want to delete this counsellor?', { confirmText: 'Delete', variant: 'danger' });
    if (confirmed) {
      try {
        await deleteCounsellor(counsellorId);
        fetchCounsellors();
      } catch (error) {
        toast.error('Error deleting counsellor: ' + error.message);
      }
    }
  };

  const handleSuspend = async (userId, isSuspended) => {
    const action = isSuspended ? 'unsuspend' : 'suspend';
    const confirmMessage = isSuspended 
      ? 'Are you sure you want to unsuspend this counsellor? They will be able to login again.'
      : 'Are you sure you want to suspend this counsellor? They will not be able to login.';
    
    const confirmed = await confirm(confirmMessage, { confirmText: isSuspended ? 'Unsuspend' : 'Suspend', variant: 'warning' });
    if (confirmed) {
      try {
        if (isSuspended) {
          await unsuspendCounsellor(userId);
        } else {
          await suspendCounsellor(userId, 'Suspended by administrator');
        }
        fetchCounsellors();
        toast.success(`Counsellor ${action}ed successfully`);
      } catch (error) {
        toast.error(`Error ${action}ing counsellor: ${error.message}`);
      }
    }
  };

  // Filter counsellors based on search query
  const filteredCounsellors = useMemo(() => {
    if (!searchQuery) return counsellors;
    
    const query = searchQuery.toLowerCase();
    return counsellors.filter(counsellor => {
      const fullName = `${counsellor.first_name} ${counsellor.last_name}`.toLowerCase();
      const email = counsellor.email?.toLowerCase() || '';
      return fullName.includes(query) || email.includes(query);
    });
  }, [counsellors, searchQuery]);

  const columns = [
    { 
      header: 'Name', 
      accessor: 'first_name',
      sortKey: 'first_name',
      sortValue: (row) => `${row.first_name} ${row.last_name}`,
      render: (row) => (
        <div>
          {`${row.first_name} ${row.last_name}`}
          {row.is_suspended && <span style={{ marginLeft: '8px', padding: '2px 8px', fontSize: '11px', background: '#ff9800', color: 'white', borderRadius: '4px', fontWeight: 'bold' }}>SUSPENDED</span>}
        </div>
      )
    },
    { header: 'Email', accessor: 'email' },
    { header: 'Phone', accessor: 'phone', sortable: false },
    { header: 'Specialization', accessor: 'specialization', sortable: false },
    { header: 'Projects Assigned', accessor: 'number_of_projects_assigned', sortable: false },
    { header: 'Experience (years)', accessor: 'years_of_experience', sortable: false },
    ...(isSuperAdmin ? [{ 
      header: 'Actions',
      sortable: false,
      render: (row) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button size="small" 
            variant={row.is_suspended ? 'primary' : 'warning'} 
            onClick={() => handleSuspend(row.user_id, row.is_suspended)} loading={loading}
            style={{ 
              backgroundColor: row.is_suspended ? '#4CAF50' : '#FF9800',
              minWidth: '90px'
            }}
          >
            {row.is_suspended ? 'Unsuspend' : 'Suspend'}
          </Button>
          <Button size="small" variant="danger" onClick={() => handleDelete(row.id)} loading={loading}>
            Delete
          </Button>
        </div>
      )
    }] : [])
  ];

  if (loading) {
    return <div className={styles.loading}><LoadingSpinner /></div>;
  }

  return (
    <div className={styles.mentorsPage}>
      <div className={styles.header}>
        <h1>Counsellors</h1>
        <div className={styles.headerActions}>
          <SearchBar 
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search counsellors by name or email..."
          />
          <Button icon="+" onClick={() => setShowModal(true)} loading={loading}>Add Counsellor</Button>
        </div>
      </div>

      <Card>
        <Table columns={columns} data={filteredCounsellors} />
      </Card>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add New Counsellor" size="large">
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>First Name *</label>
              <input name="first_name" value={values.first_name} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Last Name *</label>
              <input name="last_name" value={values.last_name} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Email *</label>
              <input type="email" name="email" value={values.email} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Phone *</label>
              <input name="phone" value={values.phone} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>LinkedIn Profile</label>
              <input name="linkedin_profile" value={values.linkedin_profile} onChange={handleChange} />
            </div>
            <div className={styles.formGroup}>
              <label>Specialization</label>
              <input name="specialization" value={values.specialization} onChange={handleChange} />
            </div>
            <div className={styles.formGroup}>
              <label>Years of Experience</label>
              <input type="number" name="years_of_experience" value={values.years_of_experience} onChange={handleChange} />
            </div>
            <div className={styles.formGroup}>
              <label>Timezone</label>
              <input name="timezone" value={values.timezone} onChange={handleChange} />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Description</label>
            <textarea name="description" value={values.description} onChange={handleChange} rows="3" />
          </div>
          <div className={styles.formActions}>
            <Button type="button" variant="outline" onClick={() => setShowModal(false)} loading={loading}>Cancel</Button>
            <Button type="submit" loading={isSubmitting}>Create Counsellor</Button>
          </div>
        </form>
      </Modal>

      {showCredentials && (
        <Modal isOpen={!!showCredentials} onClose={() => setShowCredentials(null)} title="Counsellor Credentials">
          <div className={styles.credentials}>
            <p><strong>Username:</strong> {showCredentials.username}</p>
            <p><strong>Email:</strong> {showCredentials.email}</p>
            <p><strong>Password:</strong> {showCredentials.password}</p>
            <p className={styles.warning}>Alert: Please save these credentials securely. They will not be shown again.</p>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Counsellors;

